<script setup lang="ts">
import { CalendarDays, Lock } from 'lucide-vue-next'
import {breakpointsTailwind, useBreakpoints} from "@vueuse/core";
const breakpoints = useBreakpoints(breakpointsTailwind)



interface SlotColor {
  bg: string
  text: string
  'text-secondary': string
  'text-tertiary': string
}

interface TimeSlot {
  day: string
  time: string
  available: boolean
  booked?: boolean
  type?: string | null
  subject_name?: string | null
  level_name?: string | null
  color?: SlotColor | null
  tooltip?: string
}

interface LegendItem {
  label: string
  bg: string
}

interface Props {
  tutorName: string
  availability: TimeSlot[][]
  legend?: LegendItem[]
}

const props = defineProps<Props>()

const days = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

const getSubjectName = (subjectName: string) => {
    return breakpoints.greaterOrEqual('md').value
        ? subjectName
        : subjectName.substring(0,4);
}

// Get background class for a slot
const getSlotBgClass = (slot: TimeSlot): string => {
  if (!slot.available) return 'bg-gray-100'
  if (slot.booked) return 'bg-amber-100'
  if (slot.type === 'group' && slot.color) return slot.color.bg
  if (slot.type === 'group') return 'bg-purple-100'
  return 'bg-green-100'
}

// Get text class for primary text (subject name)
const getSlotTextClass = (slot: TimeSlot): string => {
  if (slot.type === 'group' && slot.color) return slot.color.text
  return 'text-purple-700'
}

// Get text class for secondary text (level name)
const getSlotSecondaryTextClass = (slot: TimeSlot): string => {
  if (slot.type === 'group' && slot.color) return slot.color['text-secondary']
  return 'text-purple-600'
}

// Get text class for tertiary text (time)
const getSlotTertiaryTextClass = (slot: TimeSlot): string => {
  if (slot.type === 'group' && slot.color) return slot.color['text-tertiary']
  return 'text-purple-500'
}
</script>

<template>
  <section class="md:bg-white md:rounded-2xl md:shadow-sm md:p-6 top-24">
    <div class="text-center mb-4">
      <div class="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl mb-3">
        <CalendarDays class="w-6 h-6 text-white" />
      </div>
      <h3 class="text-lg font-bold text-gray-900 mb-1">Weekly Availability</h3>
      <p v-if="tutorName" class="text-gray-600 text-xs">{{ tutorName }}'s teaching schedule</p>
      <p v-else class="text-gray-600 text-xs">Teaching schedule</p>
    </div>

    <div class="mb-4">
      <div class="grid grid-cols-7 gap-1 mb-2">
        <div
          v-for="day in days"
          :key="day"
          class="text-center"
        >
          <p class="text-[10px] font-semibold mb-1 text-gray-600">
            {{ day }}
          </p>
        </div>
      </div>

      <div class="space-y-1">
        <div
          v-for="(row, rowIndex) in availability"
          :key="rowIndex"
          class="grid grid-cols-7 gap-1"
        >
          <div
            v-for="(slot, slotIndex) in row"
            :key="slotIndex"
            :title="slot.tooltip"
            :class="[
              'py-2 px-0.5 rounded flex flex-col md:items-center justify-center text-center cursor-default min-h-[52px] min-w-0',
              getSlotBgClass(slot),
            ]"
          >
            <!-- Unavailable slot: Lock icon + time -->
            <template v-if="!slot.available">
              <Lock class="w-3 h-3 text-gray-400 mb-0.5 mx-auto" />
              <span class="text-[9px] text-gray-400">{{ slot.time }}</span>
            </template>

            <!-- Booked slot: Booked + time (strikethrough) -->
            <template v-else-if="slot.booked">
              <span class="text-[9px] font-medium text-amber-700 line-through">Booked</span>
              <span class="text-[9px] text-amber-600">{{ slot.time }}</span>
            </template>

            <!-- Group session: Subject + Level + time (with dynamic colors) -->
            <template v-else-if="slot.type === 'group'">
              <p :class="['text-[9px] font-semibold', getSlotTextClass(slot)]">
                  {{ getSubjectName(slot.subject_name) || 'Group' }}
              </p>
              <p :class="['text-[9px] truncate', getSlotSecondaryTextClass(slot)]">{{ slot.level_name || '' }}</p>
              <p :class="['text-[8px]', getSlotTertiaryTextClass(slot)]">{{ slot.time }}</p>
            </template>

            <!-- 1:1 session: 1:1 + (Available) + time -->
            <template v-else>
              <span class="text-[9px] font-semibold text-green-700">1:1</span>
              <span class="text-[8px] text-green-600 truncate">(Available)</span>
              <span class="text-[8px] text-green-500">{{ slot.time }}</span>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-4 space-y-1.5">
      <div class="flex items-center gap-2">
        <div class="w-3 h-3 bg-green-100 rounded" />
        <span class="text-xs text-gray-600">1:1 Session</span>
      </div>

      <!-- Dynamic group session colors from legend -->
      <template v-if="legend && legend.length > 0">
        <div
          v-for="(item, index) in legend"
          :key="index"
          class="flex items-center gap-2"
        >
          <div :class="['w-3 h-3 rounded', item.bg]" />
          <span class="text-xs text-gray-600">{{ item.label }}</span>
        </div>
      </template>

      <!-- Fallback if no legend -->
      <div v-else class="flex items-center gap-2">
        <div class="w-3 h-3 bg-purple-100 rounded" />
        <span class="text-xs text-gray-600">Group Session</span>
      </div>

      <div class="flex items-center gap-2">
        <div class="w-3 h-3 bg-amber-100 rounded" />
        <span class="text-xs text-gray-600 line-through">Booked</span>
      </div>
      <div class="flex items-center gap-2">
        <div class="w-3 h-3 bg-gray-100 rounded" />
        <span class="text-xs text-gray-600">Not Available</span>
      </div>
    </div>
  </section>
</template>
